import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import CallNumberCard from "../../components/CallNumberCard";
import { FullPageLoading, InlineSpinner } from "../../components/Loading";
import { Eyebrow, Fleuron, HeadingItalic } from "../../components/design";
import {
  adminDownloadBookPdf,
  adminGetUser,
  adminListChapters,
  adminListCalls,
  type AdminCall,
  type AdminUserDoc,
  type ChapterSummary,
} from "../../lib/api";
import { formatTimestamp } from "../../lib/format";

export default function AdminPreview() {
  const { phone = "" } = useParams();
  const decodedPhone = decodeURIComponent(phone);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<AdminUserDoc | null>(null);
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [calls, setCalls] = useState<AdminCall[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const [u, c, t] = await Promise.all([
          adminGetUser(decodedPhone),
          adminListChapters(decodedPhone).catch(() => ({ chapters: [] })),
          adminListCalls(decodedPhone).catch(() => ({ calls: [] })),
        ]);
        setUser(u);
        setChapters((c.chapters as ChapterSummary[]) || []);
        setCalls(t.calls || []);
      } catch (e: any) {
        setError(e?.message || "Fehler beim Laden.");
      } finally {
        setLoading(false);
      }
    })();
  }, [decodedPhone]);

  if (loading) return <FullPageLoading label="Lade Kundenansicht …" />;

  const data = user?.data || {};
  const narratorFirstName = (data.interviewee_first_name as string) || "";
  const narratorLastName = (data.interviewee_last_name as string) || "";
  const narratorFullName =
    [narratorFirstName, narratorLastName].filter(Boolean).join(" ") || "—";
  const ordererFirstName = (data.orderer_first_name as string) || "";
  const ordererLastName = (data.orderer_last_name as string) || "";
  const ordererEmail = (data.orderer_email as string) || "";
  const chaptersWithContent = chapters.filter((c) => (c.content_length || 0) > 0).length;

  return (
    <div className="paper-bg" style={{ minHeight: "100vh" }}>
      <AdminHeader />

      {/* Admin preview banner */}
      <div
        style={{
          background: "var(--oxblood)",
          color: "var(--paper-50)",
          padding: "10px 22px",
          fontFamily: "var(--f-ui)",
          fontSize: 13,
          position: "sticky",
          top: 0,
          zIndex: 50,
          boxShadow: "0 2px 8px rgba(43,26,15,0.18)",
        }}
      >
        <div
          className="desk-container-app"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span>
            <strong style={{ textTransform: "uppercase", letterSpacing: "0.12em" }}>
              Admin-Vorschau
            </strong>{" "}
            · Sie sehen den Bereich von {narratorFullName}{" "}
            <span style={{ opacity: 0.7 }}>({user?.phone})</span>
          </span>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Link
              to={`/admin/users/${encodeURIComponent(decodedPhone)}`}
              style={{
                color: "var(--paper-50)",
                textDecoration: "underline",
                textUnderlineOffset: 4,
              }}
            >
              ← Zurück zur Detailansicht
            </Link>
            <Link
              to="/admin"
              style={{
                color: "var(--paper-50)",
                textDecoration: "underline",
                textUnderlineOffset: 4,
                opacity: 0.85,
              }}
            >
              Erzähler-Übersicht
            </Link>
          </div>
        </div>
      </div>

      <main
        className="desk-container-app"
        style={{ padding: "32px 22px 60px" }}
      >
        {error && (
          <p
            role="alert"
            style={{
              padding: "12px 16px",
              background: "rgba(107,32,24,0.06)",
              border: "1px solid rgba(107,32,24,0.25)",
              borderRadius: 8,
              color: "var(--oxblood)",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 14,
              marginBottom: 24,
            }}
          >
            {error}
          </p>
        )}

        {user && (
          <>
            <header
              style={{
                marginTop: 8,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <div>
                <Eyebrow>Erzähler</Eyebrow>
                <HeadingItalic size={36}>
                  <em>{narratorFullName}</em>
                </HeadingItalic>
                <p
                  style={{
                    fontFamily: "var(--f-body)",
                    color: "var(--ink-soft)",
                    fontSize: 15,
                    margin: "8px 0 0",
                  }}
                >
                  Auftraggeber: {ordererFirstName} {ordererLastName} ·{" "}
                  <a
                    href={`mailto:${ordererEmail}`}
                    style={{ color: "var(--ink-soft)" }}
                  >
                    {ordererEmail}
                  </a>
                </p>
              </div>
            </header>

            <Fleuron />

            <section
              className="desk-narrator-grid"
              style={{
                marginTop: 28,
                display: "grid",
                gap: 28,
              }}
            >
              <div>
                <CallNumberCard
                  callNumber="041037044937"
                  intervieweeFirstName={narratorFirstName}
                />

                <BookPdfRow phone={decodedPhone} />

                {/* Chapter cockpit */}
                <section style={{ marginTop: 28 }}>
                  <SectionTitle
                    title="Kapitel"
                    meta={`${chaptersWithContent} von ${chapters.length || 13} begonnen`}
                  />
                  {chapters.length === 0 ? (
                    <EmptyNote>Noch keine Kapitel angelegt.</EmptyNote>
                  ) : (
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        margin: 0,
                        display: "grid",
                        gap: 8,
                      }}
                    >
                      {chapters.map((c) => {
                        const hasContent = (c.content_length || 0) > 0;
                        const inner = (
                          <>
                            <div>
                              <div
                                style={{
                                  fontFamily: "var(--f-display)",
                                  fontWeight: 500,
                                  fontSize: 16,
                                  color: "var(--ink)",
                                }}
                              >
                                {c.title}
                              </div>
                              {c.summary && (
                                <div
                                  style={{
                                    fontFamily: "var(--f-body)",
                                    fontSize: 13,
                                    color: "var(--ink-soft)",
                                    marginTop: 2,
                                    maxWidth: 540,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {c.summary}
                                </div>
                              )}
                            </div>
                            <StatusDot
                              color={
                                c.exported
                                  ? "var(--oxblood)"
                                  : hasContent
                                  ? "var(--sage)"
                                  : "var(--ink-faint)"
                              }
                              label={
                                c.exported
                                  ? "fertig"
                                  : hasContent
                                  ? "begonnen"
                                  : "leer"
                              }
                            />
                          </>
                        );
                        const sharedStyle: React.CSSProperties = {
                          padding: "12px 16px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          textDecoration: "none",
                          color: "inherit",
                          cursor: hasContent ? "pointer" : "default",
                        };
                        return (
                          <li
                            key={c.title}
                            className="card"
                            style={{ padding: 0 }}
                          >
                            {hasContent ? (
                              <Link
                                to={`/admin/users/${encodeURIComponent(
                                  decodedPhone,
                                )}/chapters/${encodeURIComponent(c.title)}`}
                                style={sharedStyle}
                              >
                                {inner}
                              </Link>
                            ) : (
                              <div style={sharedStyle}>{inner}</div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              </div>

              {/* Call history */}
              <aside>
                <SectionTitle
                  title="Anrufe"
                  meta={
                    calls.length === 0
                      ? "noch keine"
                      : `${calls.length} insgesamt`
                  }
                />
                {calls.length === 0 ? (
                  <EmptyNote>
                    Noch hat {narratorFirstName || "der Erzähler"} nicht
                    angerufen. Die Leitung ist Tag und Nacht offen.
                  </EmptyNote>
                ) : (
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    {calls.map((c, idx) => (
                      <li
                        key={c.transcriptId || idx}
                        className="card"
                        style={{ padding: "12px 16px" }}
                      >
                        <div
                          style={{
                            fontFamily: "var(--f-display)",
                            fontWeight: 500,
                            fontSize: 15,
                            color: "var(--ink)",
                          }}
                        >
                          {formatTimestamp(c.timestamp)}
                        </div>
                        {c.duration_seconds != null && (
                          <div
                            style={{
                              fontFamily: "var(--f-body)",
                              fontSize: 13,
                              color: "var(--ink-soft)",
                              marginTop: 2,
                            }}
                          >
                            Dauer: {Math.round(c.duration_seconds / 60)} Min.
                            {!c.processed && " · wird ausgewertet"}
                          </div>
                        )}
                        {c.preview && (
                          <div
                            style={{
                              fontFamily: "var(--f-body)",
                              fontSize: 13,
                              color: "var(--ink-soft)",
                              marginTop: 6,
                              maxWidth: 520,
                              fontStyle: "italic",
                            }}
                          >
                            {c.preview}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </aside>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function SectionTitle({ title, meta }: { title: string; meta?: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 12,
      }}
    >
      <h2
        className="t-display-it"
        style={{ margin: 0, fontSize: 22 }}
      >
        {title}
      </h2>
      {meta && (
        <span
          className="t-caps"
          style={{ color: "var(--ink-faint)" }}
        >
          {meta}
        </span>
      )}
    </div>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="card"
      style={{
        padding: "16px 18px",
        fontFamily: "var(--f-body)",
        fontStyle: "italic",
        fontSize: 14,
        color: "var(--ink-soft)",
      }}
    >
      {children}
    </div>
  );
}

function StatusDot({ color, label }: { color: string; label: string }) {
  return (
    <span
      className="t-caps"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        color: "var(--ink-soft)",
        whiteSpace: "nowrap",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

function BookPdfRow({ phone }: { phone: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onClick() {
    setError(null);
    setBusy(true);
    try {
      await adminDownloadBookPdf(phone);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Download fehlgeschlagen.");
      setTimeout(() => setError(null), 6000);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 12, marginBottom: 18 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
        }}
      >
        <button
          type="button"
          onClick={onClick}
          disabled={busy}
          className="btn-secondary"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {busy ? (
            <InlineSpinner />
          ) : (
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
          )}
          {busy ? "Wird vorbereitet …" : "Als PDF"}
        </button>
        <Link
          to={`/admin/users/${encodeURIComponent(phone)}/book-text`}
          className="btn-secondary"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            textDecoration: "none",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M4 6h16M4 12h16M4 18h10" />
          </svg>
          Als Text
        </Link>
      </div>
      {error && (
        <p
          role="alert"
          style={{
            marginTop: 6,
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 12.5,
            color: "var(--error)",
            textAlign: "center",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}
